/**
 * 🛡️ G2eGente - Layout do Candidato (Web)
 * Layout unificado igual ao app mobile
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI, solicitationsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Home,
  Briefcase,
  FileText,
  Users,
  MessageSquare,
  User,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Brain,
  ChevronRight
} from 'lucide-react';

const menuItems = [
  { path: '/candidate', icon: Home, label: 'Início' },
  { path: '/candidate/jobs', icon: Briefcase, label: 'Explorar Vagas' },
  { path: '/candidate/applications', icon: FileText, label: 'Minhas Candidaturas' },
  { path: '/candidate/solicitations', icon: Users, label: 'Solicitações de Contato' },
  { path: '/candidate/chats', icon: MessageSquare, label: 'Mensagens' },
  { path: '/candidate/profile', icon: User, label: 'Meu Perfil' },
  { path: '/candidate/disc', icon: Brain, label: 'Teste DISC' },
  { path: '/candidate/notifications', icon: Bell, label: 'Notificações' },
  { path: '/candidate/settings', icon: Settings, label: 'Configurações' },
];

export default function CandidateLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [pendingSolicitations, setPendingSolicitations] = useState(0);

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const [notifRes, solicRes] = await Promise.all([
        notificationsAPI.getAll(),
        solicitationsAPI.getReceived()
      ]);
      setUnreadNotifications(notifRes.data.filter(n => !n.lida).length);
      setPendingSolicitations(solicRes.data.filter(s => s.status === 'PENDING').length);
    } catch (error) {
      console.log('Error fetching counts');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/candidate') {
      return location.pathname === '/candidate';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-gradient-to-r from-slate-900 to-slate-800 text-white sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <Link to="/candidate" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center font-bold text-white">
              G2
            </div>
            <span className="font-bold text-lg">G2eGente</span>
          </Link>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/candidate/notifications')}
              className="relative p-2"
            >
              <Bell className="w-6 h-6" />
              {unreadNotifications > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadNotifications}
                </span>
              )}
            </button>
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-gradient-to-b from-slate-900 to-slate-800">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <Link to="/candidate" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500 rounded-xl flex items-center justify-center font-bold text-white text-lg">
                G2
              </div>
              <span className="font-bold text-xl text-white">G2eGente</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const showBadge = 
                (item.path === '/candidate/notifications' && unreadNotifications > 0) ||
                (item.path === '/candidate/solicitations' && pendingSolicitations > 0);
              const badgeCount = item.path === '/candidate/notifications' 
                ? unreadNotifications 
                : pendingSolicitations;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    active 
                      ? 'bg-cyan-600 text-white' 
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {showBadge && (
                    <Badge className="bg-red-500 text-white text-xs">
                      {badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                <p className="text-xs text-slate-400">Candidato</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sair
            </Button>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="absolute right-0 top-0 bottom-0 w-80 bg-gradient-to-b from-slate-900 to-slate-800 shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <span className="font-bold text-lg text-white">Menu</span>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 text-slate-400"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="p-4 space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const showBadge = 
                    (item.path === '/candidate/notifications' && unreadNotifications > 0) ||
                    (item.path === '/candidate/solicitations' && pendingSolicitations > 0);
                  const badgeCount = item.path === '/candidate/notifications' 
                    ? unreadNotifications 
                    : pendingSolicitations;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        active 
                          ? 'bg-cyan-600 text-white' 
                          : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1">{item.label}</span>
                      {showBadge && (
                        <Badge className="bg-red-500 text-white text-xs">
                          {badgeCount}
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </Link>
                  );
                })}
              </nav>

              {/* Logout */}
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Sair da Conta
                </Button>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-72">
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40">
        <div className="flex justify-around py-2">
          {[
            { path: '/candidate', icon: Home, label: 'Início' },
            { path: '/candidate/jobs', icon: Briefcase, label: 'Vagas' },
            { path: '/candidate/applications', icon: FileText, label: 'Candidaturas' },
            { path: '/candidate/solicitations', icon: Users, label: 'Solicitações', badge: pendingSolicitations },
            { path: '/candidate/profile', icon: User, label: 'Perfil' },
          ].map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center py-1 px-3 relative ${
                  active ? 'text-cyan-600' : 'text-slate-400'
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs mt-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute top-0 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
